import { useCallback } from 'react';

import type { EPackageTiers } from '@feathr/blackbox';
import { logUserEvents } from '@feathr/hooks';

export interface IFeatureAccessEvent {
  eventType: 'blocked_nav_click' | 'blocked_nav_hover';
  featureName: string;
  requiredPackage?: EPackageTiers;
  userId?: string;
  accountId?: string;
  currentPackage?: string;
  [key: string]: unknown;
}

export interface IUseFeatureAccessAnalyticsOptions {
  disabled?: boolean;
}

export interface IAnalyticsContext {
  userId?: string;
  accountId?: string;
  currentPackage?: string;
}

export interface ITrackBlockedNavFunctionProps {
  (
    featureName: string,
    requiredPackage?: EPackageTiers,
    contextData?: Record<string, unknown>,
  ): void;
}

export function useBlockedNavAnalytics(
  context: IAnalyticsContext = {},
  options: IUseFeatureAccessAnalyticsOptions = {},
): {
  trackBlockedNavClick: ITrackBlockedNavFunctionProps;
  trackBlockedNavHover: ITrackBlockedNavFunctionProps;
} {
  const { userId, accountId, currentPackage } = context;
  const { disabled = false } = options;

  const trackEvent = useCallback(
    (
      eventType: 'blocked_nav_click' | 'blocked_nav_hover',
      featureName: string,
      requiredPackage?: EPackageTiers,
      contextData?: Record<string, unknown>,
    ) => {
      if (disabled) {
        return;
      }

      try {
        const event: IFeatureAccessEvent = {
          eventType,
          featureName,
          requiredPackage,
          userId,
          accountId,
          currentPackage,
          ...contextData,
        };

        logUserEvents({ [eventType]: event });
      } catch (error) {
        // Silently handle analytics errors to not break user experience
        // eslint-disable-next-line no-console
        console.warn('Failed to track feature access event:', error);
      }
    },
    [disabled, userId, accountId, currentPackage],
  );

  const trackBlockedNavClick = useCallback(
    (
      featureName: string,
      requiredPackage?: EPackageTiers,
      contextData?: Record<string, unknown>,
    ) => {
      trackEvent('blocked_nav_click', featureName, requiredPackage, contextData);
    },
    [trackEvent],
  );

  const trackBlockedNavHover = useCallback(
    (
      featureName: string,
      requiredPackage?: EPackageTiers,
      contextData?: Record<string, unknown>,
    ) => {
      trackEvent('blocked_nav_hover', featureName, requiredPackage, contextData);
    },
    [trackEvent],
  );

  return {
    trackBlockedNavClick,
    trackBlockedNavHover,
  };
}
